/**
 * The Up next line: what the reader pressed while one thought was in flight.
 *
 * One small row per press, under the live pass wherever the live pass shows —
 * the thought line over the field, or the feed's own tail in the thread column
 * — so the second and third press are visible as taken, in the order they will
 * run, each with the estimate it was pressed against and a ✕ that takes it off.
 * A press that could not run when its turn came comes back as a chip that asks
 * again, carrying the reason and today's figure; nothing it shows runs without
 * a new press (`core/upNext.ts`).
 *
 * A rewrite and a rethink are named as their buttons name them — "Rewrite
 * “<label>”", "Rethink “<branch>”" — and a waiting rewrite keeps saying what its
 * press said it replaces. On a chip that asks again, both read their terms off
 * the graph as it is now, because the chip's press is held to what the chip
 * shows (`takeUpNext`).
 *
 * Not a live region. The press that queued a row said so in a toast, the app's
 * one announcing surface, and a second region carrying the same words would
 * read every press twice.
 */

import type { CSSProperties } from 'react'
import { useStore } from '../core/store'
import { fmtUsd } from '../core/models'
import { rewriteLoss, rewriteLossPhrase } from '../core/rewrite'
import { nextRethink, upNextCalled, upNextHere, upNextName, type UpNextItem } from '../core/upNext'
import { OP_LABEL, type Session } from '../core/types'
import { quotedPress, STATUS_WORD } from '../core/vocabulary'
import { dropUpNext, takeUpNext } from './upNextPress'
import { THOUGHT_TINT } from './Overlay'
import { Icon } from './components/Icon'

export function UpNext({ inFeed = false }: { inFeed?: boolean }) {
  const upNext = useStore((s) => s.upNext)
  const session = useStore((s) => s.session)
  return <UpNextLine items={upNextHere({ session, upNext })} session={session} inFeed={inFeed} />
}

/**
 * The line with the store read above it. Exported for its test, the way
 * `SurveyCard` is: a server render only ever sees the store's initial state.
 * The whole document, because a chip's rewrite and rethink read their terms
 * off it.
 */
export function UpNextLine({
  items,
  session,
  inFeed = false,
}: {
  items: UpNextItem[]
  session: Session
  inFeed?: boolean
}) {
  if (items.length === 0) return null

  let waiting = 0
  return (
    <div
      className={inFeed ? 'upnext upnext--feed' : 'upnext'}
      role="group"
      aria-label={STATUS_WORD.upNext}
    >
      {items.map((item) => {
        const tint = THOUGHT_TINT[item.verb]
        if (item.held) return <HeldRow key={item.id} item={item} session={session} tint={tint} />
        const first = waiting++ === 0
        const face = waitingFace(item, session)
        const row = (
          <div key={item.id} className="upnext-row">
            <span className="upnext-eyebrow">{first ? STATUS_WORD.upNext : 'Then'}</span>
            <span className="upnext-what" title={face.title}>
              <b style={{ color: tint } as CSSProperties}>{face.name}</b>
              {face.clause}
            </span>
            {item.est !== null && (
              <span
                className="upnext-est"
                title="What this press was estimated at when you made it — at list rates, leaning high on purpose."
              >
                est. +{fmtUsd(item.est)}
              </span>
            )}
            <Unqueue item={item} name={face.called} />
          </div>
        )
        // A line of its own under the row, where the row's ellipsis cannot
        // take it: the thread column is narrow, and the count is the promise.
        return face.note ? (
          <div key={item.id}>
            {row}
            <p className="upnext-why">{face.note}</p>
          </div>
        ) : (
          row
        )
      })}
    </div>
  )
}

/**
 * A waiting row's words: the name in the verb's tint, and what follows it. A
 * move is its verb and its claim; a rewrite and a rethink are their buttons'
 * own names, and a rewrite adds, under the row, what its press said it
 * replaces — the count it is held to — with the reader's wording a hover away.
 */
function waitingFace(
  item: UpNextItem,
  session: Pick<Session, 'nodes'>,
): { name: string; clause: string; called: string; title?: string; note?: string } {
  const called = upNextCalled(item)
  if (item.verb === 'revise') {
    const lost = rewriteLossPhrase(item.loss)
    return {
      name: called!,
      clause: '',
      called: called!,
      title: `Your wording: ${item.draft.claim}`,
      note: lost ? `Replaces ${lost}.` : 'Replaces only the wording.',
    }
  }
  if (item.verb === 'rebase')
    return {
      name: called!,
      clause: '',
      called: called!,
      title: session.nodes[item.branch.id]?.claim,
    }
  const name = upNextName(item.verb, session.nodes[item.nodeId]?.parentId === null)
  return {
    name,
    clause: ` · ${item.label}`,
    called: `${name} on ${item.label}`,
    title: session.nodes[item.nodeId]?.claim,
  }
}

/** The ✕, named for the press it takes off. */
function Unqueue({ item, name }: { item: UpNextItem; name: string }) {
  return (
    <button
      type="button"
      className="upnext-x"
      aria-label={`Take ${name} off up next`}
      title="Take it off — nothing is spent"
      onClick={() => dropUpNext(item.id)}
    >
      <Icon name="close" size={10} />
    </button>
  )
}

/**
 * A press whose turn came and went: the chip that asks again. A rewrite's and a
 * rethink's chips read their terms off the graph NOW — the branch a rethink
 * would take, and, under the reason, what a rewrite would replace — because
 * those are the terms the chip's press is held to, never the ones that just
 * failed to hold. Their names are long, so they give way to the figure and the
 * ask rather than wrapping inside the pill.
 */
function HeldRow({ item, session, tint }: { item: UpNextItem; session: Session; tint: string }) {
  let name: string
  let label: string | null = null
  let called: string
  let now = ''
  if (item.verb === 'revise') {
    name = quotedPress(OP_LABEL.revise, session.nodes[item.nodeId]?.label ?? item.label)
    const lost = rewriteLossPhrase(rewriteLoss(session, item.nodeId))
    now = ` Pressed now, it replaces ${lost ?? 'only the wording'}.`
    called = name
  } else if (item.verb === 'rebase') {
    name = quotedPress(OP_LABEL.rebase, (nextRethink(session, item.nodeId) ?? item.branch).label)
    called = name
  } else {
    name = upNextName(item.verb, session.nodes[item.nodeId]?.parentId === null)
    label = item.label
    called = `${name} on ${item.label}`
  }
  return (
    <div className="upnext-held">
      <div className="upnext-row">
        <button
          type="button"
          className="upnext-chip"
          title={item.held}
          onClick={() => takeUpNext(item.id)}
        >
          <b
            className={label === null ? 'upnext-chip-label' : undefined}
            style={{ color: tint } as CSSProperties}
          >
            {name}
          </b>
          {label !== null && <span className="upnext-chip-label">{label}</span>}
          {item.est !== null && <span className="upnext-est">est. +{fmtUsd(item.est)}</span>}
          <span className="upnext-chip-ask">Run it?</span>
        </button>
        <Unqueue item={item} name={called} />
      </div>
      <p className="upnext-why">
        {item.held}
        {now}
      </p>
    </div>
  )
}
